import { useEffect, useState } from "react";

import type { KpiFormat } from "../lib/format";
import { asKpiFormat, formatValue, inferFormatFromName } from "../lib/format";
import { GATE_COPY, resolveTileGate } from "../lib/sanityGate";
import { EMPTY_RANGE_COPY } from "./KPITile";
import {
  type KpiComparisonField,
  type KpiComparisonPane,
  type ManifestWidget,
} from "../lib/manifest";
import { getSupabase } from "../lib/supabase";
import { useTenantId } from "../lib/tenant";
import { scopeTenantTable } from "../lib/tenantScope.mjs";
import {
  applyFeedQuality,
  applyFilters,
  resolveLatestFilters,
  applyNowCutoff,
  applyTimeline,
  canonicalColumnName,
  resolveSourceName,
} from "../lib/queries";

/**
 * 3-pane comparison tile (manifest type=kpi, kpi_subtype=kpi_comparison).
 * Each pane either declares column+aggregation or a simple arithmetic
 * expression over aggregations on the same source. Used by Revenue tab
 * 1.3.1 FL P&L, 1.3.2 RO P&L, 1.3.3 SL P&L (Earned / Disposal / Net Margin).
 */

export interface KpiComparisonTileProps {
  widget: ManifestWidget;
  field: KpiComparisonField;
  format: KpiFormat;
  caption?: string;
}

interface PaneState {
  value: number | null;
  isLoading: boolean;
  error: string | null;
  // MER-317: mirrors KpiResult.isEmpty. True when the pane's underlying
  // aggregate(s) returned null/undefined for every input, meaning the
  // selected range had zero rows. The Pane component swaps in the
  // limited-activity copy instead of bare "n/a" when this is set.
  isEmpty: boolean;
}

// MER-317: aggregate runners surface an isEmpty signal alongside the
// numeric result so the pane can distinguish "no rows in range" from a
// real zero (e.g. a 0% margin computed from a divisor that did have
// rows). value === null carries no signal on its own.
interface PaneAggregateResult {
  value: number | null;
  isEmpty: boolean;
}

const SIMPLE_EXPRESSION =
  /^\s*(sum|avg|count|min|max)\(([a-z0-9_]+)\)\s*([+\-/*])\s*(sum|avg|count|min|max)\(([a-z0-9_]+)\)\s*$/i;

async function runPostgrestAggregate(
  table: string,
  rawColumn: string,
  agg: string,
  widget: ManifestWidget,
  tenantId: string,
): Promise<PaneAggregateResult> {
  // MER-589: resolve any {op:"latest"} pin before the pane query builds.
  widget = await resolveLatestFilters(widget, tenantId);
  const supabase = getSupabase();
  const column = canonicalColumnName(rawColumn);
  // Supabase v2's select() validates the template-literal column expression
  // at the type level, which doesn't accept dynamic strings. queries.ts
  // works around this with `SupabaseFilter = any`; do the same here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q: any = scopeTenantTable(
    (supabase.from(table) as any).select(`${column}.${agg}()`),
    tenantId,
    table,
  );
  // Apply the widget's filter + timeline so panes respect equipment_type
  // and similar slicers. Without these, FL/RO/SL P&L tiles all return
  // the same aggregate over the entire table (MER-131 regression).
  q = applyFilters(q, widget);
  q = applyTimeline(q, widget);
  q = applyNowCutoff(q, widget, table);
  // MER-693: a pane averaging kpi_summary rows drops a starved period for the
  // same reason a KPI tile does. No-op for sums, counts and every table
  // without a data_quality column.
  q = applyFeedQuality(q, table, agg);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  if (!Array.isArray(data) || data.length === 0) {
    return { value: null, isEmpty: true };
  }
  const row = data[0] as Record<string, unknown>;
  const v = row[agg] ?? row[column];
  if (v === null || v === undefined) {
    // MER-317: null aggregate on sum/avg/min/max means PostgREST saw zero
    // input rows. count is special: PostgREST returns 0 (not null) when
    // no rows match, so treat count===0 as the empty case below.
    return { value: null, isEmpty: true };
  }
  const n = Number(v);
  if (Number.isNaN(n)) return { value: null, isEmpty: false };
  const isEmpty = agg === "count" ? n === 0 : false;
  return { value: n, isEmpty };
}

async function resolvePane(
  pane: KpiComparisonPane,
  widget: ManifestWidget,
  tenantId: string,
): Promise<PaneAggregateResult> {
  if (!widget.source) return { value: null, isEmpty: false };
  const table = resolveSourceName(widget.source);

  if (pane.column && pane.aggregation) {
    return runPostgrestAggregate(table, pane.column, pane.aggregation, widget, tenantId);
  }

  if (pane.expression) {
    const m = SIMPLE_EXPRESSION.exec(pane.expression);
    if (!m) return { value: null, isEmpty: false };
    const [, agg1, col1, op, agg2, col2] = m;
    const [a, b] = await Promise.all([
      runPostgrestAggregate(table, col1, agg1.toLowerCase(), widget, tenantId),
      runPostgrestAggregate(table, col2, agg2.toLowerCase(), widget, tenantId),
    ]);
    // MER-317: if either operand has no rows, the composite is empty too.
    // A composite where both operands have rows but produce a zero (e.g.
    // a 0% margin from a real cost / real revenue calc) is still a valid
    // measurement and renders normally.
    const isEmpty = a.isEmpty || b.isEmpty;
    if (a.value === null || b.value === null) {
      return { value: null, isEmpty };
    }
    switch (op) {
      case "+":
        return { value: a.value + b.value, isEmpty };
      case "-":
        return { value: a.value - b.value, isEmpty };
      case "*":
        return { value: a.value * b.value, isEmpty };
      case "/":
        return { value: b.value === 0 ? null : a.value / b.value, isEmpty };
      default:
        return { value: null, isEmpty: false };
    }
  }

  return { value: null, isEmpty: false };
}

// MER-180 per-pane format resolution, mirroring MeasurePivotTable's
// measureFormat priority: explicit pane.display_format wins, then the
// column/label naming heuristic, then the widget-level format. Without
// this every pane rendered with the widget format, so a mixed rollup
// (days + percentage, e.g. L1b.12 Payment Health) mis-formatted all but
// one pane.
function paneFormat(pane: KpiComparisonPane, widgetFormat: KpiFormat): KpiFormat {
  if (pane.display_format) return asKpiFormat(pane.display_format);
  return inferFormatFromName(pane.column, pane.label) ?? widgetFormat;
}

function Pane(props: { pane: KpiComparisonPane; state: PaneState; format: KpiFormat }) {
  const { pane, state, format } = props;
  // MER-317: empty-range panes swap the formatted number for the
  // limited-activity copy. The pane label keeps rendering so the
  // reader still sees which slice was empty (e.g. "Earned" vs
  // "Disposal"). We also let the empty message wrap inside the pane
  // since the standard nowrap+ellipsis would clip the sentence.
  const isEmpty = state.isEmpty && !state.isLoading && !state.error;
  // MER-462: a pane query failure must NEVER surface as raw red "err" in
  // front of a customer (same raw-artifact class as the MER-446/449
  // bans). Render the standard gate copy in the muted treatment; the
  // underlying message stays in the title attribute for debugging.
  // MER-462: panes also get the same egress gate as single-number tiles
  // (FORCE_REVIEW / bounds / review band) so an impossible figure cannot
  // hide inside a rollup pane when every sibling tile would clamp it.
  const gate =
    !state.isLoading && !state.error && !isEmpty
      ? resolveTileGate(state.value, format, { column: pane.column })
      : null;
  const muted = Boolean(state.error || isEmpty || gate);
  const mutedCopy = state.error ? GATE_COPY : gate ? gate.copy : EMPTY_RANGE_COPY;
  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "var(--text-muted)",
          marginBottom: 4,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {pane.label}
      </div>
      <div
        style={{
          fontSize: muted ? 11 : 18,
          fontWeight: muted ? 500 : 600,
          color: muted ? "var(--text-muted)" : "var(--text-primary)",
          whiteSpace: muted ? "normal" : "nowrap",
          overflow: "hidden",
          textOverflow: muted ? undefined : "ellipsis",
          lineHeight: 1.3,
        }}
        title={state.error ?? undefined}
      >
        {state.isLoading ? "…" : muted ? mutedCopy : formatValue(state.value, format)}
      </div>
    </div>
  );
}

export default function KpiComparisonTile(props: KpiComparisonTileProps) {
  const { widget, field, format, caption } = props;
  const panes: { key: keyof KpiComparisonField; pane: KpiComparisonPane }[] = [];
  if (field.primary) panes.push({ key: "primary", pane: field.primary });
  if (field.secondary) panes.push({ key: "secondary", pane: field.secondary });
  if (field.tertiary) panes.push({ key: "tertiary", pane: field.tertiary });

  const [states, setStates] = useState<Record<string, PaneState>>(() =>
    Object.fromEntries(
      panes.map((p) => [p.key, { value: null, isLoading: true, error: null, isEmpty: false }]),
    ),
  );

  const tenantId = useTenantId();

  useEffect(() => {
    const next: Record<string, PaneState> = {};
    for (const p of panes) {
      next[p.key] = { value: null, isLoading: true, error: null, isEmpty: false };
    }
    setStates(next);
    if (!tenantId) return;
    const boundTenantId = tenantId;
    let cancelled = false;

    Promise.all(
      panes.map(async (p) => {
        try {
          const r = await resolvePane(p.pane, widget, boundTenantId);
          // MER-317: propagate isEmpty so the Pane component can render
          // the limited-activity copy instead of bare "n/a" when the
          // selected range produced no rows for this pane's aggregate(s).
          return {
            key: p.key,
            state: {
              value: r.value,
              isLoading: false,
              error: null,
              isEmpty: r.isEmpty,
            },
          };
        } catch (err) {
          return {
            key: p.key,
            state: {
              value: null,
              isLoading: false,
              error: err instanceof Error ? err.message : String(err),
              isEmpty: false,
            },
          };
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const merged: Record<string, PaneState> = {};
      for (const r of results) {
        merged[r.key] = r.state;
      }
      setStates(merged);
    });

    return () => {
      cancelled = true;
    };
    // panes list is derived from widget.field; widget identity is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget, tenantId]);

  return (
    <article
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: "20px 22px",
        minWidth: 200,
        // MER-181: card fills its grid cell so sibling cards in the
        // same row align top + bottom (grid default align-items
        // stretches the wrapper; height:100% pulls the article to fill).
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <header
        style={{
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "var(--text-muted)",
          marginBottom: 12,
        }}
      >
        {widget.name}
      </header>
      <div style={{ display: "flex", gap: 16 }}>
        {panes.map((p) => (
          <Pane
            key={p.key}
            pane={p.pane}
            state={states[p.key] ?? { value: null, isLoading: true, error: null, isEmpty: false }}
            format={paneFormat(p.pane, format)}
          />
        ))}
      </div>
      {/* MER-181: caption slot reserves vertical space even when the
          manifest omits `what_it_shows` so cards with and without
          descriptions match heights in the same row. `marginTop: auto`
          pushes the caption to the bottom of the flex column when the
          card is taller than its intrinsic content. */}
      <div
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          marginTop: "auto",
          paddingTop: 10,
          minHeight: 32,
          lineHeight: 1.35,
        }}
      >
        {caption ?? ""}
      </div>
    </article>
  );
}
